import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
    Chip,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";

/* Custom Components — import from your project */
// import RadioOption from "../../../components/RadioOption";
// import Typography from "../../../components/Typography";
// import LectureTimer from "../../../components/LectureTimer";
// import LanguageTabs from "../../../components/LanguageTabs";

/* ─── Background ─────────────────────────────────────────────────────────── */
const bgImage =
    "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1600&q=80";

/* RN brand colour */
const RN_PRIMARY   = "gold";   /*  — RN shares it */
const RN_ACCENT    = "goldenrod";   /*  for mobile flavour  */
const RN_DARK      = "#0E0A1A";

/* ─── Styled Components — exact mirror of Linux / React Dashboard ───────── */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(14,10,26,0.93), rgba(14,10,26,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

const DashboardGlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(14,10,26,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.08)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "all 0.25s ease",
    "&:hover": {
        backgroundColor: "rgba(255,107,53,0.08)",
        borderColor: RN_ACCENT,
        transform: "translateX(3px)",
    },
}));

const ViewButton = styled(Button)(() => ({
    backgroundColor: RN_ACCENT,
    color: "#fff",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
        backgroundColor: "#e55a28",
        transform: "scale(1.05)",
    },
}));

/* ─── Inline RadioOption ─────────────────────────────────────────────────── */
function RadioOption({ checked, onChange }) {
    return (
        <Box
            onClick={onChange}
            sx={{
                cursor: "pointer",
                color: checked ? RN_ACCENT : "rgba(255,255,255,0.25)",
                flexShrink: 0, display: "flex", alignItems: "center",
            }}
        >
            {checked
                ? <CheckCircleOutlineIcon fontSize="small" />
                : <RadioButtonUncheckedIcon fontSize="small" />}
        </Box>
    );
}

/* ─── Inline LectureTimer ────────────────────────────────────────────────── */
function LectureTimer() {
    const [seconds, setSeconds] = useState(0);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        if (!running) return;
        const id = setInterval(() => setSeconds(s => s + 1), 1000);
        return () => clearInterval(id);
    }, [running]);

    const fmt = s =>
        `${String(Math.floor(s / 3600)).padStart(2, "0")}:` +
        `${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:` +
        `${String(s % 60).padStart(2, "0")}`;

    return (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, px: 2 }}>
            <Box sx={{
                display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                justifyContent: "center",
                backgroundColor: "rgba(255,107,53,0.06)",
                border: "1px solid rgba(255,107,53,0.18)",
                borderRadius: "999px", px: 3, py: 1,
            }}>
                <Box sx={{ color: RN_ACCENT, fontSize: "0.7rem", fontWeight: 700, letterSpacing: 1 }}>
                    STUDY TIMER
                </Box>
                <Box sx={{ fontFamily: "monospace", fontSize: "1.05rem", color: "white", minWidth: "82px", textAlign: "center" }}>
                    {fmt(seconds)}
                </Box>
                <Button size="small" onClick={() => setRunning(r => !r)} sx={{
                    backgroundColor: running ? "rgba(255,80,80,0.12)" : "rgba(255,107,53,0.12)",
                    color: running ? "#FF5050" : RN_ACCENT,
                    borderRadius: "999px", px: 2, textTransform: "none",
                    fontWeight: 700, fontSize: "0.72rem",
                    "&:hover": { backgroundColor: running ? "rgba(255,80,80,0.22)" : "rgba(255,107,53,0.22)" },
                }}>
                    {running ? "Pause" : "Start"}
                </Button>
                <Button size="small" onClick={() => { setSeconds(0); setRunning(false); }} sx={{
                    color: "rgba(255,255,255,0.35)", borderRadius: "999px",
                    px: 1.5, textTransform: "none", fontSize: "0.72rem",
                    "&:hover": { color: "white" },
                }}>
                    Reset
                </Button>
            </Box>
        </Box>
    );
}

/* ─── Inline LanguageTabs ────────────────────────────────────────────────── */
const TABS = [
    { label: "React",        path: "/react-dashboard" },
    { label: "React Native", path: "/react-native-dashboard" },
    { label: "Angular",      path: "/angular-dashboard" },
];

function LanguageTabs({ active }) {
    return (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4, gap: 1.5, flexWrap: "wrap", px: 2 }}>
            {TABS.map(tab => (
                <Link key={tab.label} to={tab.path} style={{ textDecoration: "none" }}>
                    <Button sx={{
                        borderRadius: "999px", px: 3, py: 0.8,
                        fontWeight: 700, fontSize: "0.8rem", textTransform: "none",
                        backgroundColor: active === tab.label ? RN_ACCENT : "rgba(255,255,255,0.05)",
                        color: active === tab.label ? "#fff" : "rgba(255,255,255,0.55)",
                        border: "1px solid",
                        borderColor: active === tab.label ? "transparent" : "rgba(255,255,255,0.08)",
                        "&:hover": {
                            backgroundColor: active === tab.label ? "#e55a28" : "rgba(255,107,53,0.08)",
                            color: active === tab.label ? "#fff" : RN_ACCENT,
                            borderColor: "rgba(255,107,53,0.3)",
                        },
                    }}>
                        {tab.label}
                    </Button>
                </Link>
            ))}
        </Box>
    );
}

/* ─── React Native Topics — all chapters from the PDF book ──────────────── */
const rnTopics = [
    // Chapter 1 — Introduction to RN
    { title: "What is React Native? Web vs Mobile",                   link: "/rn/intro",                  chapter: "Intro" },
    { title: "Setting Up Expo (Recommended for Beginners)",           link: "/rn/setup-expo",             chapter: "Intro" },
    { title: "Setting Up React Native CLI (Advanced)",                link: "/rn/setup-cli",              chapter: "Intro" },
    { title: "Project Structure & File Overview",                     link: "/rn/project-structure",      chapter: "Intro" },
    { title: "React vs React Native — Component Equivalents",         link: "/rn/rn-vs-react",            chapter: "Intro" },
    // Chapter 2 — Core Components
    { title: "View — The Foundation Layout Component",                link: "/rn/view",                   chapter: "Core Components" },
    { title: "Text — Displaying & Styling Text",                      link: "/rn/text",                   chapter: "Core Components" },
    { title: "Image — Local & Remote Images",                         link: "/rn/image",                  chapter: "Core Components" },
    { title: "TextInput — Forms & Keyboard Input",                    link: "/rn/textinput",              chapter: "Core Components" },
    { title: "ScrollView — Scrollable Containers",                    link: "/rn/scrollview",             chapter: "Core Components" },
    { title: "FlatList — Performant Long Lists",                      link: "/rn/flatlist",               chapter: "Core Components" },
    { title: "SectionList — Grouped Data Lists",                      link: "/rn/sectionlist",            chapter: "Core Components" },
    { title: "TouchableOpacity & Pressable",                          link: "/rn/touchable",              chapter: "Core Components" },
    { title: "Modal — Overlays & Popups",                             link: "/rn/modal",                  chapter: "Core Components" },
    { title: "ActivityIndicator — Loading States",                    link: "/rn/activity-indicator",     chapter: "Core Components" },
    // Chapter 3 — Styling
    { title: "StyleSheet.create() — RN Styles",                       link: "/rn/stylesheet",             chapter: "Styling" },
    { title: "Flexbox Layout in React Native",                        link: "/rn/flexbox",                chapter: "Styling" },
    { title: "Dimensions API & Responsive Design",                    link: "/rn/dimensions",             chapter: "Styling" },
    { title: "Platform-Specific Styles (iOS vs Android)",             link: "/rn/platform-styles",        chapter: "Styling" },
    { title: "Shadows, Borders & Elevation",                          link: "/rn/shadows",                chapter: "Styling" },
    // Chapter 4 — Navigation
    { title: "React Navigation — Setup & Overview",                   link: "/rn/nav-setup",              chapter: "Navigation" },
    { title: "Stack Navigator — Screen Transitions",                  link: "/rn/stack-nav",              chapter: "Navigation" },
    { title: "Bottom Tab Navigator",                                  link: "/rn/tab-nav",                chapter: "Navigation" },
    { title: "Drawer Navigator",                                      link: "/rn/drawer-nav",             chapter: "Navigation" },
    { title: "Passing Params Between Screens",                        link: "/rn/nav-params",             chapter: "Navigation" },
    { title: "Deep Linking & URL Handling",                           link: "/rn/deep-linking",           chapter: "Navigation" },
    // Chapter 5 — State & Hooks
    { title: "useState & useEffect in React Native",                  link: "/rn/hooks-basics",           chapter: "State & Hooks" },
    { title: "Context API for Global State",                          link: "/rn/context-api",            chapter: "State & Hooks" },
    { title: "Redux Toolkit in React Native",                         link: "/rn/redux",                  chapter: "State & Hooks" },
    { title: "Zustand — Lightweight RN State",                        link: "/rn/zustand",                chapter: "State & Hooks" },
    // Chapter 6 — Device Features & APIs
    { title: "AsyncStorage — Persistent Local Data",                  link: "/rn/async-storage",          chapter: "Device APIs" },
    { title: "Camera & Image Picker (Expo)",                          link: "/rn/camera",                 chapter: "Device APIs" },
    { title: "Geolocation & Maps",                                    link: "/rn/geolocation",            chapter: "Device APIs" },
    { title: "Push Notifications with Expo",                          link: "/rn/notifications",          chapter: "Device APIs" },
    { title: "Accelerometer & Device Sensors",                        link: "/rn/sensors",                chapter: "Device APIs" },
    { title: "Linking — Phone, Email, URLs",                          link: "/rn/linking",                chapter: "Device APIs" },
    // Chapter 7 — Networking
    { title: "Fetch API & Axios in React Native",                     link: "/rn/networking",             chapter: "Networking" },
    { title: "REST API Integration",                                  link: "/rn/rest-api",               chapter: "Networking" },
    { title: "WebSockets & Real-Time Data",                           link: "/rn/websockets",             chapter: "Networking" },
    { title: "Offline Support & NetInfo",                             link: "/rn/offline",                chapter: "Networking" },
    // Chapter 8 — Animations
    { title: "Animated API — Fade, Slide, Scale",                     link: "/rn/animated-api",           chapter: "Animations" },
    { title: "React Native Reanimated",                               link: "/rn/reanimated",             chapter: "Animations" },
    { title: "Gesture Handler",                                       link: "/rn/gesture-handler",        chapter: "Animations" },
    // Chapter 9 — Performance
    { title: "FlatList Optimisation — keyExtractor & getItemLayout",  link: "/rn/flatlist-perf",          chapter: "Performance" },
    { title: "memo, useCallback & useMemo in RN",                     link: "/rn/rn-perf-hooks",          chapter: "Performance" },
    { title: "Hermes JavaScript Engine",                              link: "/rn/hermes",                 chapter: "Performance" },
    { title: "Profiling with Flipper & DevTools",                     link: "/rn/profiling",              chapter: "Performance" },
    // Chapter 10 — Testing & Deployment
    { title: "Jest & React Native Testing Library",                   link: "/rn/testing",                chapter: "Testing" },
    { title: "Testing Navigation & Async Code",                       link: "/rn/testing-nav",            chapter: "Testing" },
    { title: "Building for iOS (Xcode & TestFlight)",                 link: "/rn/build-ios",              chapter: "Deployment" },
    { title: "Building for Android (APK & Play Store)",               link: "/rn/build-android",          chapter: "Deployment" },
    { title: "Expo EAS Build & Submit",                               link: "/rn/eas-build",              chapter: "Deployment" },
    // Chapter 11 — Projects
    { title: "Project: Notes App (AsyncStorage)",                     link: "/rn/project-notes",          chapter: "Projects" },
    { title: "Project: Weather App (API + Location)",                 link: "/rn/project-weather",        chapter: "Projects" },
    { title: "Project: Todo App with Redux",                          link: "/rn/project-todo",           chapter: "Projects" },
    { title: "Project: Social Feed with FlatList",                    link: "/rn/project-feed",           chapter: "Projects" },
    { title: "Project: Full Auth Flow (Login + Protected Routes)",    link: "/rn/project-auth",           chapter: "Projects" },
];

const chapterColors = {
    "Intro":           "#61DAFB",
    "Core Components": "#FF6B35",
    "Styling":         "#A78BFA",
    "Navigation":      "#34D399",
    "State & Hooks":   "#F59E0B",
    "Device APIs":     "#F87171",
    "Networking":      "#60A5FA",
    "Animations":      "#E879F9",
    "Performance":     "#4ADE80",
    "Testing":         "#FCD34D",
    "Deployment":      "#FB923C",
    "Projects":        "#FBBF24",
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function ReactNativeDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery,   setSearchQuery]   = useState("");
    const [showScroll,    setShowScroll]    = useState(false);
    const [completed,     setCompleted]     = useState({});
    const [activeChapter, setActiveChapter] = useState("All");

    const completedCount  = Object.values(completed).filter(Boolean).length;
    const progressPercent = (completedCount / rnTopics.length) * 100;
    const allChapters     = ["All", ...Object.keys(chapterColors)];

    /* Logic: Search Filtering & Progress */
    const filteredTopics = useMemo(() => {
        return rnTopics.filter(t => {
            const matchSearch  = t.title.toLowerCase().includes(searchQuery.toLowerCase());
            const matchChapter = activeChapter === "All" || t.chapter === activeChapter;
            return matchSearch && matchChapter;
        });
    }, [searchQuery, activeChapter]);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
    const toggleComplete  = title => setCompleted(prev => ({ ...prev, [title]: !prev[title] }));

    /* ── RENDER ──────────────────────────────────────────────────────────── */
    return (
        <PageWrapper>

            {/* ── BANNER ── */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Box
                    sx={{
                        fontWeight: 800,
                        color: RN_ACCENT,
                        fontSize: isMobile ? "1.4rem" : "2.7rem",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 30px rgba(255,107,53,0.25)",
                        letterSpacing: "0.04em",
                    }}
                >
                    📱 MASTERING REACT NATIVE
                </Box>
                <Box sx={{ mt: 1.5, color: "rgba(255,255,255,0.5)", fontSize: "0.92rem" }}>
                    Cross-Platform Mobile Development · iOS & Android · Expo & RN CLI
                </Box>
                <Box sx={{ mt: 0.8, color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
                    By{" "}
                    <Box component="span" sx={{ color: RN_ACCENT, fontWeight: 700 }}>
                        Ajao Hazzan
                    </Box>{" "}
                    · Full-Stack Software Engineer
                </Box>
            </Box>

            {/* ── TIMER & TABS ── */}
            <LectureTimer />
            <LanguageTabs active="React Native" />

            {/* ── MAIN CONTENT ── */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* ── LEFT COLUMN: Overview & Image ── */}
                <Grid item xs={12} md={5}>
                    <DashboardGlassCard>
                        <Box sx={{
                            fontWeight: 700, textAlign: "center", mb: 2,
                            color: RN_ACCENT, fontSize: "0.9rem", letterSpacing: "0.08em",
                        }}>
                            REACT NATIVE FUNDAMENTALS
                        </Box>

                        <Card sx={{
                            width: "100%",
                            background: "linear-gradient(135deg, #0D1117, #1A0D0A)",
                            textAlign: "center",
                            padding: "15px",
                            borderRadius: "12px",
                            mt: 2,
                            border: "1px solid rgba(255,107,53,0.15)",
                        }}>
                            <CardMedia
                                component="img"
                                height={isMobile ? "180" : "220"}
                                image="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a7/React-icon.svg/1200px-React-icon.svg.png"
                                alt="React Native Logo"
                                sx={{ objectFit: "contain", p: 2, filter: "hue-rotate(20deg)" }}
                            />

                            <CardContent sx={{ color: "white" }}>
                                <Box sx={{ fontWeight: 800, fontSize: "1rem", mb: 0.5, color: RN_ACCENT }}>
                                    iOS & Android Development
                                </Box>
                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <PhoneIphoneIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.5)" }} />
                                    <Box sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>
                                        One Codebase · Two Platforms
                                    </Box>
                                </Stack>

                                <Link to="/rn-playground" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<CodeIcon />}
                                        sx={{
                                            background: `linear-gradient(135deg, ${RN_ACCENT}, #e55a28)`,
                                            color: "#fff",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            textTransform: "none",
                                            fontSize: "0.9rem",
                                            "&:hover": { background: "linear-gradient(135deg, #e55a28, #FF6B35)" },
                                        }}
                                    >
                                        Practice RN Components
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>

                        {/* Stats grid */}
                        <Grid container spacing={1.5} sx={{ mt: 2 }}>
                            {[
                                { label: "Total Topics", value: rnTopics.length,      color: RN_ACCENT },
                                { label: "Completed",    value: completedCount,        color: "#34D399" },
                                { label: "Chapters",     value: Object.keys(chapterColors).length, color: "#A78BFA" },
                                { label: "Progress",     value: `${Math.round(progressPercent)}%`, color: "#F59E0B" },
                            ].map(s => (
                                <Grid item xs={6} key={s.label}>
                                    <Box sx={{
                                        backgroundColor: "rgba(255,255,255,0.04)",
                                        border: "1px solid rgba(255,255,255,0.07)",
                                        borderRadius: "10px", p: 1.5, textAlign: "center",
                                    }}>
                                        <Box sx={{ fontSize: "1.5rem", fontWeight: 800, color: s.color }}>{s.value}</Box>
                                        <Box sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{s.label}</Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Chapter legend */}
                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)", mb: 1.5, fontWeight: 700, letterSpacing: "0.1em" }}>
                                CHAPTER GUIDE
                            </Box>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {Object.entries(chapterColors).map(([ch, col]) => (
                                    <Box
                                        key={ch}
                                        onClick={() => setActiveChapter(activeChapter === ch ? "All" : ch)}
                                        sx={{
                                            display: "flex", alignItems: "center", gap: 0.6,
                                            cursor: "pointer",
                                            opacity: activeChapter === "All" || activeChapter === ch ? 1 : 0.4,
                                            transition: "opacity 0.2s",
                                        }}
                                    >
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: col, flexShrink: 0 }} />
                                        <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.55)" }}>{ch}</Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </DashboardGlassCard>
                </Grid>

                {/* ── RIGHT COLUMN: Curriculum with Progress & Search ── */}
                <Grid item xs={12} md={7}>
                    <DashboardGlassCard>
                        <Box sx={{ fontWeight: 700, textAlign: "center", mb: 3, letterSpacing: "0.06em", fontSize: "0.9rem" }}>
                            CURRICULUM MODULES
                        </Box>

                        {/* Progress Tracker Section */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Box sx={{ color: RN_ACCENT, fontWeight: "bold", fontSize: "0.75rem" }}>
                                    React Native Mastery
                                </Box>
                                <Box sx={{ color: RN_ACCENT, fontWeight: "bold", fontSize: "0.75rem" }}>
                                    {Math.round(progressPercent)}%
                                </Box>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercent}
                                sx={{
                                    height: 8, borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.08)",
                                    "& .MuiLinearProgress-bar": {
                                        background: `linear-gradient(90deg, ${RN_ACCENT}, #e55a28)`,
                                        borderRadius: 5,
                                    },
                                }}
                            />
                            <Box sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.3)", mt: 0.8, textAlign: "right" }}>
                                {completedCount} of {rnTopics.length} topics completed
                            </Box>
                        </Box>

                        {/* Chapter filter chips */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2.5 }}>
                            {allChapters.map(ch => (
                                <Chip
                                    key={ch}
                                    label={ch}
                                    size="small"
                                    onClick={() => setActiveChapter(ch)}
                                    sx={{
                                        cursor: "pointer",
                                        backgroundColor: activeChapter === ch
                                            ? (chapterColors[ch] || RN_ACCENT)
                                            : "rgba(255,255,255,0.05)",
                                        color: activeChapter === ch ? (ch === "All" ? "#0A0C1E" : "#0A0C1E") : "rgba(255,255,255,0.55)",
                                        fontWeight: activeChapter === ch ? 700 : 500,
                                        fontSize: "0.68rem",
                                        border: "1px solid",
                                        borderColor: activeChapter === ch ? "transparent" : "rgba(255,255,255,0.07)",
                                        "&:hover": {
                                            backgroundColor: chapterColors[ch]
                                                ? `${chapterColors[ch]}28`
                                                : "rgba(255,107,53,0.1)",
                                        },
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Search Component */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search React Native topics..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: RN_ACCENT, fontSize: "1.1rem" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.04)",
                                    borderRadius: "8px",
                                    fontSize: "0.88rem",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,107,53,0.2)" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,107,53,0.4)" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: RN_ACCENT },
                                },
                            }}
                            inputProps={{ style: { color: "white" } }}
                        />

                        {/* Topic list */}
                        <Box sx={{
                            maxHeight: "520px", overflowY: "auto", pr: 0.5,
                            "&::-webkit-scrollbar": { width: "4px" },
                            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                            "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255,107,53,0.25)", borderRadius: "4px" },
                        }}>
                            <Stack spacing={1.5}>
                                {filteredTopics.length > 0 ? (
                                    filteredTopics.map((topic, i) => (
                                        <TopicBox key={i}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1, minWidth: 0 }}>
                                                <RadioOption
                                                    checked={!!completed[topic.title]}
                                                    onChange={() => toggleComplete(topic.title)}
                                                />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Box sx={{
                                                        fontSize: "0.85rem", fontWeight: 500,
                                                        color: completed[topic.title] ? "rgba(255,255,255,0.35)" : "white",
                                                        textDecoration: completed[topic.title] ? "line-through" : "none",
                                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    }}>
                                                        {topic.title}
                                                    </Box>
                                                    <Box sx={{
                                                        fontSize: "0.62rem", fontWeight: 600, mt: 0.2,
                                                        color: chapterColors[topic.chapter] || RN_ACCENT,
                                                    }}>
                                                        {topic.chapter}
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Link to={topic.link} style={{ textDecoration: "none" }}>
                                                <ViewButton size="small">View</ViewButton>
                                            </Link>
                                        </TopicBox>
                                    ))
                                ) : (
                                    <Box sx={{ textAlign: "center", opacity: 0.4, py: 4, fontSize: "0.9rem" }}>
                                        No modules found for "{searchQuery}"
                                    </Box>
                                )}
                            </Stack>
                        </Box>

                        {/* Footer row */}
                        <Box sx={{
                            mt: 2, pt: 2,
                            borderTop: "1px solid rgba(255,255,255,0.06)",
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", flexWrap: "wrap", gap: 1,
                        }}>
                            <Box sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)" }}>
                                {filteredTopics.length} of {rnTopics.length} topics shown
                            </Box>
                            {completedCount > 0 && (
                                <Box
                                    onClick={() => setCompleted({})}
                                    sx={{ fontSize: "0.7rem", color: "rgba(255,80,80,0.5)", cursor: "pointer", "&:hover": { color: "#FF5050" } }}
                                >
                                    Reset progress
                                </Box>
                            )}
                        </Box>
                    </DashboardGlassCard>
                </Grid>
            </Grid>

            {/* Back to Top Floating Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{
                        position: "fixed", bottom: 25, right: 25,
                        background: `linear-gradient(135deg, ${RN_ACCENT}, #e55a28)`,
                        "&:hover": { background: "linear-gradient(135deg, #e55a28, #FF6B35)" },
                    }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#fff" }} />
                </Fab>
            </Zoom>

        </PageWrapper>
    );
}
